/*
 * (c) Copyright, 2023, Medidata Solutions Worldwide, Inc. All rights reserved.
 */
import * as yup from 'yup'
import { isArray } from 'underscore'
import { APPROVAL_ACTION_TYPE, RA_ACTION_TYPE, WF_STEP_PROCESSING_TYPE, WF_STEP_TYPE } from '~/types/workflow'

// Type definitions for validation schemas
interface TaskTeamStepLabel {
  id?: number | null
  label?: string | null
  value?: number | null
}

interface UsersGroupsItem {
  id?: number
  name?: string
  type?: string
}

interface StepData {
  taskTeamStepLabel?: TaskTeamStepLabel | null
  processingType?: string | null
  nofAllValue?: number | null
  usersGroups?: UsersGroupsItem[]
}

// Set custom locale messages
yup.setLocale({
  mixed: {
    required: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
  }
})

// Add custom validation method for empty checks
declare module 'yup' {
  interface MixedSchema {
    isNotEmpty(errorText?: string): this
  }
}

yup.addMethod(yup.mixed, 'isNotEmpty', function (this: any, errorText?: string) {
  return this.test('isNotEmpty', errorText || 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR', function (values: any) {
    let valid = true

    if (!values || values === '') {
      valid = false
    } else if (isArray(values) && !values.length) {
      valid = false
    }

    return valid
  })
})

const taskTeamStepLabel = yup
  .object()
  .shape({
    id: yup.number().nullable(),
    label: yup.string().nullable(),
    value: yup.number().nullable()
  })
  .nullable()
  .default(null)

export const nofAllValue = (translate: (key: string) => string) => {
  const someAtOnceErrorMessage = translate('BROWSER_FILE_SETTINGS_CR_APPROVERS_ERROR_EQUAL_OR_MORE_THAN_1')
  return yup
    .mixed()
    .when('processingType', {
      is: WF_STEP_PROCESSING_TYPE.N_OF_ALL,
      then: (_schema: any) =>
        yup
          .number()
          .min(1, someAtOnceErrorMessage)
          .nullable()
          .transform((value: any) => (isNaN(value) ? undefined : value))
          .required(),
      otherwise: (schema: any) => schema.nullable()
    })
    .nullable()
}

export const AuthorizationValidationSchema = (translate: (key: string) => string) => {
  return yup.object().shape({
    initialApprovers: yup.object().shape({
      type: yup.string().oneOf(Object.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.NOT_DEFINED),
      steps: yup.mixed().when('type', {
        is: WF_STEP_TYPE.USE_PREDEFINED,
        then: (_schema: any) =>
          yup
            .array()
            .of(
              yup.object().shape({
                taskTeamStepLabel: taskTeamStepLabel,
                processingType: yup.string().nullable().required(),
                nofAllValue: nofAllValue(translate),
                usersGroups: yup.array()
              })
            )
            .nullable()
            .default([]),
        otherwise: (_schema: any) => yup.mixed().notRequired()
      })
    })
  })
}

export const CollaboratorsValidationSchema = (translate: (key: string) => string) => {
  return yup.object().shape({
    collaborators: yup.object().shape({
      type: yup
        .string()
        .oneOf(Object.values(WF_STEP_TYPE))
        .default(() => WF_STEP_TYPE.USE_INITIATOR),
      steps: yup.mixed().when('type', {
        is: WF_STEP_TYPE.USE_PREDEFINED,
        then: (_schema: any) =>
          yup
            .array()
            .of(
              yup.object().shape({
                taskTeamStepLabel: taskTeamStepLabel,
                processingType: yup.string().nullable().required(),
                nofAllValue: nofAllValue(translate),
                usersGroups: yup.array().required().min(1, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
              })
            )
            .nullable()
            .default(() => []),
        otherwise: (_schema: any) => yup.mixed().notRequired()
      })
    })
  })
}

export const ReviewersValidationSchema = (translate: (key: string) => string) => {
  return yup.object().shape({
    reviewers: yup.object().shape({
      type: yup.string().oneOf(Object.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.NOT_DEFINED),
      steps: yup.mixed().when('type', {
        is: WF_STEP_TYPE.USE_PREDEFINED,
        then: (_schema: any) =>
          yup
            .array()
            .of(
              yup.object().shape({
                taskTeamStepLabel: taskTeamStepLabel,
                processingType: yup.string().nullable().required(),
                nofAllValue: nofAllValue(translate),
                usersGroups: yup.array()
              })
            )
            .nullable()
            .default([]),
        otherwise: (_schema: any) => yup.mixed().notRequired()
      })
    })
  })
}

export const RAValidationSchema = () => {
  return yup.object().shape({
    trainees: yup.object().shape({
      createRAActionType: yup.string().oneOf(Object.values(RA_ACTION_TYPE)).default(RA_ACTION_TYPE.FOR_NOBODY),
      usersGroups: yup.array().default([]).nullable()
    })
  })
}

export const RejectionReviewersValidationSchema = () => {
  return yup.object().shape({
    rejectionReviewers: yup.object().shape({
      type: yup.string().oneOf(Object.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.USE_INITIATOR),
      usersGroups: yup
        .array()
        .when('type', {
          is: WF_STEP_TYPE.USE_PREDEFINED,
          then: (_schema: any) => yup.array().required().min(1, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'),
          otherwise: (schema: any) => schema
        })
        .default([])
        .nullable()
    })
  })
}

export const ApprovalValidationSchema = (translate: (key: string) => string) => {
  return yup.object().shape({
    approvers: yup.object().shape({
      type: yup.string().oneOf(Object.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.NOT_DEFINED),
      approveActions: yup
        .array()
        .of(
          yup.object().shape({
            actionType: yup.string().nullable(),
            actionData: yup
              .mixed()
              .when('actionType', {
                is: APPROVAL_ACTION_TYPE.SET_PROPERTY,
                then: (_schema: any) =>
                  yup
                    .array()
                    .of(
                      yup.object().shape({
                        property: yup.mixed().nullable(),
                        metadataTemplateType: yup.string().nullable(),
                        val: yup.mixed().when('metadataTemplateType', {
                          is: 'ARTIFACT',
                          then: (_schema: any) => yup.mixed().nullable(),
                          otherwise: (_schema: any) =>
                            yup.mixed().isNotEmpty('BROWSER_FILE_SETTINGS_APPROVERS_ERROR_PLEASE_ENTER_VALUE')
                        }),
                        value: yup.mixed().nullable(),
                        metadataTemplateId: yup
                          .number()
                          .nullable()
                          .required('BROWSER_FILE_SETTINGS_APPROVERS_ERROR_PLEASE_SELECT_PROPERTY')
                      })
                    )
                    .nullable()
                    .required(),
                otherwise: (schema: any) => schema
              })
              .when('actionType', {
                is: APPROVAL_ACTION_TYPE.MOVE,
                then: (_schema: any) =>
                  yup
                    .object()
                    .shape({
                      id: yup
                        .number()
                        .nullable()
                        .required('BROWSER_FILE_SETTINGS_APPROVERS_ERROR_PLEASE_SELECT_FOLDER'),
                      name: yup
                        .string()
                        .nullable()
                        .required('BROWSER_FILE_SETTINGS_APPROVERS_ERROR_PLEASE_SELECT_FOLDER')
                    })
                    .nullable()
                    .required('BROWSER_FILE_SETTINGS_APPROVERS_ERROR_PLEASE_SELECT_FOLDER'),
                otherwise: (schema: any) => schema
              })
              .when('actionType', {
                is: APPROVAL_ACTION_TYPE.COPY,
                then: (_schema: any) =>
                  yup
                    .object()
                    .shape({
                      id: yup
                        .number()
                        .nullable()
                        .required('BROWSER_FILE_SETTINGS_APPROVERS_ERROR_PLEASE_SELECT_FOLDER'),
                      name: yup
                        .string()
                        .nullable()
                        .required('BROWSER_FILE_SETTINGS_APPROVERS_ERROR_PLEASE_SELECT_FOLDER')
                    })
                    .nullable()
                    .required('BROWSER_FILE_SETTINGS_APPROVERS_ERROR_PLEASE_SELECT_FOLDER'),
                otherwise: (schema: any) => schema
              })
              .nullable(),
            usersGroups: yup
              .array()
              .when('actionType', {
                is: APPROVAL_ACTION_TYPE.NOTIFY,
                then: (_schema: any) =>
                  yup.array().nullable().required('BROWSER_FILE_SETTINGS_APPROVERS_ERROR_SELECT_USERS_GROUPS'),
                otherwise: (schema: any) => schema
              })
              .nullable()
          })
        )
        .nullable()
        .default([]),
      steps: yup.mixed().when('type', {
        is: WF_STEP_TYPE.USE_PREDEFINED,
        then: (_schema: any) =>
          yup
            .array()
            .of(
              yup.object().shape({
                taskTeamStepLabel: taskTeamStepLabel,
                processingType: yup.string().nullable().required(),
                nofAllValue: nofAllValue(translate),
                usersGroups: yup.array()
              })
            )
            .nullable()
            .default([]),
        otherwise: (_schema: any) => yup.mixed().notRequired()
      })
    })
  })
}
